// slices/activity/reducer.ts (or activitySlice.ts)

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { 
    fetchAssignedMembers, 
    assignMemberToTarget, 
    removeMemberFromTarget 
} from "./thunk"; // Assuming thunks are in the same directory or adjust path

// --- Interfaces ---

// Data structure for an assigned member (must match the thunk's return type)
interface AssignedMember {
    id: string; // The assignment record ID (UUID)
    user: string; // The user ID
    user_name: string; // The user's name
    initials: string; // Calculated initials
    bg: string; // Display color
    color: string; // Display text color
}

// State structure for the slice
interface ActivityState {
    assignedMembers: AssignedMember[];
    assignedMembersLoading: boolean;
    assignedMembersError: string | null;
}

// Initial State
const initialState: ActivityState = {
    assignedMembers: [],
    assignedMembersLoading: false,
    assignedMembersError: null,
};

// =========================================================================
// Activity Slice
// =========================================================================

const activitySlice = createSlice({
    name: "activity",
    initialState,
    reducers: {
        // You can add other synchronous reducers here if needed
    },
    extraReducers: (builder) => {
        // --- 1. Fetch Assigned Members ---
        builder
            .addCase(fetchAssignedMembers.pending, (state) => {
                state.assignedMembersLoading = true;
                state.assignedMembersError = null;
            })
            .addCase(fetchAssignedMembers.fulfilled, (state, action: PayloadAction<AssignedMember[]>) => {
                state.assignedMembersLoading = false;
                // Replace the current list with the fresh data from the backend
                state.assignedMembers = action.payload;
            })
            .addCase(fetchAssignedMembers.rejected, (state, action) => {
                state.assignedMembersLoading = false;
                state.assignedMembersError = action.payload ? String(action.payload) : "Failed to load assigned members.";
            });

        // --- 2. Assign Member (Mutation) ---
        // For assignment, we typically only need to handle loading/error status, 
        // as the UI will immediately trigger a 'fetchAssignedMembers' after success.
        builder
            .addCase(assignMemberToTarget.pending, (state) => {
                state.assignedMembersError = null;
                // Note: We often use a separate 'isAssigning' state (handled in the component)
                // but setting a general loading state here is fine too.
            })
            .addCase(assignMemberToTarget.fulfilled, (state, action: PayloadAction<AssignedMember>) => {
                state.assignedMembersError = null;
                // OPTIONAL: If you want to optimistically add the member before re-fetch:
                // state.assignedMembers.push(action.payload);
            })
            .addCase(assignMemberToTarget.rejected, (state, action) => {
                state.assignedMembersError = action.payload ? String(action.payload) : "Failed to assign member.";
            });

        // --- 3. Remove Member (Mutation) ---
        // Similar to assign, handling only loading/error status, relying on re-fetch for data update.
        builder
            .addCase(removeMemberFromTarget.pending, (state) => {
                state.assignedMembersError = null;
            })
            .addCase(removeMemberFromTarget.fulfilled, (state, action: PayloadAction<string>) => {
                state.assignedMembersError = null;
                // OPTIONAL: If you want to optimistically remove the member before re-fetch:
                // state.assignedMembers = state.assignedMembers.filter(m => m.id !== action.payload);
            })
            .addCase(removeMemberFromTarget.rejected, (state, action) => {
                state.assignedMembersError = action.payload ? String(action.payload) : "Failed to remove member.";
            });

    },
});

// export const { } = activitySlice.actions;

export default activitySlice.reducer;
